import type { AttributeSource } from '../types/api';

/** Where a contact's name, language or country came from, in plain words. */
export const ATTRIBUTE_SOURCE_LABEL: Record<AttributeSource, string> = {
  import: 'from an import',
  signup: 'from a signup form',
  order: 'from an order',
  self: 'set by the contact',
};

/** What each recorded consent category means, in plain words. */
const CONSENT_CATEGORY_LABEL: Record<string, string> = {
  confirmed_double_optin: 'Double opt-in',
  single_optin_with_time_and_ip: 'Single opt-in (time + IP)',
  time_only: 'Signup time only',
  no_evidence: 'No evidence',
};

/**
 * The contact's basis for getting the newsletter. A buyer put on the list
 * by an order has no consent category (the record stays honest), so the
 * order is shown as the basis instead of a dash.
 */
export function consentLabel(category: string | null, orderSignupAt: number | null): string {
  if (category) return CONSENT_CATEGORY_LABEL[category] ?? category;
  return orderSignupAt !== null ? 'Order' : '—';
}

/** Two-letter language code shown with its English name when the runtime knows it (`da` -> "da (Danish)"). */
export function languageLabel(code: string | null): string {
  if (!code) return '—';
  try {
    const name = new Intl.DisplayNames(['en'], { type: 'language' }).of(code);
    return name && name !== code ? `${code} (${name})` : code;
  } catch {
    return code;
  }
}

/** ISO 3166-1 alpha-2 code shown with its English name when the runtime knows it (`DK` -> "DK (Denmark)"). */
export function countryLabel(code: string | null): string {
  if (!code) return '—';
  try {
    const name = new Intl.DisplayNames(['en'], { type: 'region' }).of(code);
    return name && name !== code ? `${code} (${name})` : code;
  } catch {
    return code;
  }
}

/**
 * Splits a comma/space separated list of codes into distinct, normalised
 * codes. This only tidies what was typed; the server is the one that
 * validates (ISO 3166-1 for countries), and its error is shown.
 */
export function parseCodeList(text: string, casing: 'lower' | 'upper'): string[] {
  const out: string[] = [];
  for (const part of text.split(/[\s,;]+/)) {
    const code = casing === 'lower' ? part.toLowerCase() : part.toUpperCase();
    if (code !== '' && !out.includes(code)) out.push(code);
  }
  return out;
}
